/**
 * Drone leveling system — single source of truth for XP thresholds and level derivation.
 *
 * Design (see docs/mechanics/drone_levels.md for the full rationale):
 *  - Every drone has an integer xp counter persisted in the drones table.
 *  - Level is **derived** from xp via levelFromXp() — never persisted (would diverge).
 *  - Levels are capped at 4; L4 stays L4 (awarding code clamps xp at the L4 threshold).
 *  - Existing drones (xp=0) map to L1 with all multipliers at 1.0, so the introduction
 *    of this system is a no-op for the running fleet until they accumulate xp.
 *
 * THE NUMBERS BELOW ARE PROVISIONAL. They assume:
 *  - A T1 station miner mining T1 asteroids continuously at ~10 HP/s with a 75% duty
 *    cycle → ~27 000 XP/h → ~3.7 days AFK to L4.
 *  - Pendiente de pendiente entre niveles ≈ 1 : 2.5 : 4.5 (semi-linear, no exponential wall).
 *
 * Retune once we have real drone-mortality data: L4 should remain RARE relative to the
 * observed median drone lifespan. If drones rarely die, lengthen the curve; if they die
 * fast, shorten it. See drone_levels.md "regla de oro".
 */

export const DRONE_LEVEL_MAX = 4 as const;

export type DroneLevel = 1 | 2 | 3 | 4;

/**
 * Cumulative XP required to BE at each level.
 *  - index 0 → L1 boundary (xp >= 0)
 *  - index 1 → L2 boundary (xp >= 300_000)
 *  - index 2 → L3 boundary (xp >= 1_050_000)
 *  - index 3 → L4 boundary (xp >= 2_400_000)  ← cap
 *
 * Gaps follow a 1 : 2.5 : 4.5 slope (L1→L2 = 300K, L2→L3 = 750K, L3→L4 = 1.35M).
 */
export const DRONE_LEVEL_XP_THRESHOLDS: readonly [0, 300_000, 1_050_000, 2_400_000] = [
  0,
  300_000,
  1_050_000,
  2_400_000,
] as const;

/** XP value at which the L4 cap is reached; awarding code clamps here. */
export const DRONE_XP_CAP: number = DRONE_LEVEL_XP_THRESHOLDS[3];

/**
 * Derive level from cumulative XP. O(1) — at most 3 comparisons.
 *
 * Negative xp is treated as 0 (defensive — shouldn't happen, the column has a CHECK).
 */
export function levelFromXp(xp: number): DroneLevel {
  if (xp >= DRONE_LEVEL_XP_THRESHOLDS[3]) return 4;
  if (xp >= DRONE_LEVEL_XP_THRESHOLDS[2]) return 3;
  if (xp >= DRONE_LEVEL_XP_THRESHOLDS[1]) return 2;
  return 1;
}

/**
 * XP remaining to reach the next level. Returns 0 once at L4 (no overflow tracking).
 * Used by the owner's drone-bay UI to draw a progress bar.
 */
export function xpToNextLevel(xp: number): number {
  const level = levelFromXp(xp);
  if (level === 1) return DRONE_LEVEL_XP_THRESHOLDS[1] - xp;
  if (level === 2) return DRONE_LEVEL_XP_THRESHOLDS[2] - xp;
  if (level === 3) return DRONE_LEVEL_XP_THRESHOLDS[3] - xp;
  return 0; // L4 cap
}

// ============================================================
// STAT SCALING
// ============================================================

import type { DroneType, DroneStats, DroneSpec } from './drones.js';
import { MINING_TOOLS, WEAPONS } from './equipment.js';

/**
 * Per-level multipliers on a drone's baseline (L1) hull/shield/speed stats.
 * Indexed by `level - 1`. L1 entries are always 1.0 → loading existing drones
 * (xp=0 → L1) produces identical stats to pre-feature behavior.
 *
 * Hull/shield 1→3× chosen to put L4 in the "2-3 shots instead of 1" zone vs
 * baseline. Speed 1→1.5× kept gentle — higher would make L4 fighters un-kiteable
 * and L4 miners farm asteroids disproportionately by cutting travel time.
 */
export const DRONE_LEVEL_MULTIPLIERS = {
  hull:   [1.0, 1.4, 2.0, 3.0],
  shield: [1.0, 1.4, 2.0, 3.0],
  speed:  [1.0, 1.15, 1.30, 1.50],
} as const;

/**
 * Hauler-specific level multipliers. Cargo capacity is the hauler's defining
 * upside — L4 carries 3× the L1 baseline. (4× was floated and rejected: it
 * compounds with margin-per-hour XP and ran ahead of miner/fighter timelines.)
 *
 * Docking penalty timer scaling was discussed but the mechanic doesn't exist
 * in the codebase yet; if/when it lands, add it here.
 */
export const HAULER_LEVEL_MULTIPLIERS = {
  cargoCapacity: [1.0, 1.5, 2.2, 3.0],
} as const;

/**
 * Per-type stat hooks — declarative replacement for `if (drone.type === 'x')`
 * chains in the level-aware stats path. Each hook receives the current level
 * and the BASELINE (L1) stats from DRONE_SPECS, and returns a partial override
 * of stats that depend on the drone's type and level.
 *
 * Adding a new drone type = one entry here. `getEffectiveDroneStats` does NOT
 * need to be touched.
 *
 *  - miner:   level → MINING_TOOLS[`tool_mining_t${level}`]. Reuses the player
 *             mining tool table (single source of truth for mining progression).
 *             Passes the tier to computeMiningTierMultiplier for asymmetric
 *             penalty on cross-tier asteroids. See docs/mechanics/mining.md.
 *  - hauler:  cargo scales with HAULER_LEVEL_MULTIPLIERS.
 *  - fighter: weapon damage/range scale by the ratio between WEAPONS.weapon_kinetic_t1
 *             and weapon_kinetic_tN. Cooldown is the inverse of player fireRate
 *             (player tier ↑ → fireRate ↓ → cooldown ↑). Net DPS at L4 ≈ 3×,
 *             matching hull/shield 3× for coherent scaling.
 */
export const DRONE_TYPE_STAT_HOOKS: Record<
  DroneType,
  (level: DroneLevel, base: DroneStats) => Partial<DroneStats>
> = {
  miner: (level, _base) => {
    const tool = MINING_TOOLS[`tool_mining_t${level}`];
    if (!tool) return {};
    return {
      miningTier:  tool.tier,
      miningPower: tool.stats.miningPower,
      miningRange: tool.stats.range,
    };
  },
  hauler: (level, base) => ({
    cargoCapacity: base.cargoCapacity * HAULER_LEVEL_MULTIPLIERS.cargoCapacity[level - 1]!,
  }),
  fighter: (level, base) => {
    if (
      base.weaponDamage === undefined
      || base.weaponRange === undefined
      || base.weaponCooldownMs === undefined
    ) return {};
    const t1 = WEAPONS.weapon_kinetic_t1?.stats;
    const tN = WEAPONS[`weapon_kinetic_t${level}`]?.stats;
    if (!t1 || !tN) return {};
    return {
      weaponDamage:     base.weaponDamage     * (tN.damage / t1.damage),
      weaponRange:      base.weaponRange      * (tN.range / t1.range),
      // cooldownMs scales inverse to fireRate (T4 fires slower).
      weaponCooldownMs: base.weaponCooldownMs * (t1.fireRate / tN.fireRate),
    };
  },
};

/**
 * Compute the drone's effective stats at its current level. Pure function:
 * (droneType, level) → DroneStats. The runtime layer caches this per AI
 * instance and invalidates on level change to keep the tick allocation-free.
 *
 * Call shape accepts a minimal `{ droneType, level }` so spawn sites can call
 * it before the full DroneState is wired up.
 */
export function getEffectiveDroneStats(
  drone: { droneType: DroneType; level: number },
  spec: DroneSpec,
): DroneStats {
  const lvl = (drone.level >= 1 && drone.level <= DRONE_LEVEL_MAX
    ? drone.level
    : 1) as DroneLevel;
  const m = DRONE_LEVEL_MULTIPLIERS;
  const base = spec.stats;
  const shared: DroneStats = {
    ...base,
    hull:   base.hull   * m.hull[lvl - 1]!,
    shield: base.shield * m.shield[lvl - 1]!,
    speed:  base.speed  * m.speed[lvl - 1]!,
  };
  const typeOverrides = DRONE_TYPE_STAT_HOOKS[drone.droneType](lvl, base);
  return { ...shared, ...typeOverrides };
}

// ============================================================
// XP REWARDS (consumed by C5 wiring)
// ============================================================

/**
 * What event grants XP for each drone type, and how much. Discriminated by
 * `kind` so a single `awardXp(drone, event)` dispatcher handles all three.
 *
 *  - perHpMined:      miner — XP = hpAccumulated × multiplier, credited on dump.
 *                     HP captures difficulty (Platinum has 2× HP of Iron, so 2× XP).
 *  - perCreditMargin: hauler — XP = floor(saleMargin × multiplier), credited on
 *                     each successful sale. Rewards real arbitrage, not touring.
 *  - perDamageToHull: fighter — XP = damageAppliedToHull × perDamagePoint, capped
 *                     PER TARGET at the target's max HP+shield (kills regen-tanking).
 *                     Plus perKill on confirmed kill of a valid hostile target.
 *
 * The "no isRogue filter" rule is design (docs/mechanics/drone_levels.md):
 * rogue miners/haulers naturally cap their XP at conversion because their
 * tickRogue doesn't enter mining/sale state. Rogue fighters DO keep
 * progressing — by design, since long-lived rogue fighters are the
 * emergent endgame threat.
 */
export type DroneXpRewards =
  | { kind: 'perHpMined';      multiplier: number }
  | { kind: 'perCreditMargin'; multiplier: number }
  | { kind: 'perDamageToHull'; perDamagePoint: number; perKill: number };

export const DRONE_XP_REWARDS: Record<DroneType, DroneXpRewards> = {
  miner:   { kind: 'perHpMined',      multiplier: 1 },
  hauler:  { kind: 'perCreditMargin', multiplier: 1 },
  fighter: { kind: 'perDamageToHull', perDamagePoint: 5, perKill: 100 },
};
